import type { Accessoire, Expression, Mouvement } from "./minerve-dessin";

/**
 * Les pages d'erreur de la charte Minerve (section 07) : un code, un titre tiré du vocabulaire
 * parlementaire, une explication factuelle et toujours une sortie. Titres, textes et libellés de
 * boutons sont ceux de la charte, mot pour mot.
 *
 * Seuls 404, 500 et 403 ont un chemin réel dans l'application. 401 (session expirée) et 594 passent
 * par des toasts, 429 n'a pas de chemin : leurs pages de la charte ne sont pas branchées.
 */
export type ErreurMinerve = {
  /** Étiquette au-dessus du titre : « Erreur 404 ». */
  code: string;
  titre: string;
  texte: string;
  expression: Expression;
  mouvements: readonly Mouvement[];
  accessoire?: Accessoire;
  /** Libellé du bouton principal. */
  cta: string;
  /** Libellé du bouton secondaire, quand la charte en prévoit un. */
  cta2?: string;
};

export const ERREURS = {
  404: {
    code: "Erreur 404",
    titre: "Cette page a quitté l'hémicycle",
    texte:
      "Minerve ne la trouve nulle part. Elle a peut-être été déplacée ou supprimée. Code de l'erreur : 404.",
    expression: "perplexe",
    mouvements: ["tilt", "blink", "scan"],
    accessoire: "loupe",
    cta: "Recharger la page",
    cta2: "Retour à l'accueil",
  },
  500: {
    code: "Erreur 500",
    titre: "Suspension de séance",
    texte:
      "Une erreur s'est produite. Rechargez la page ; si l'erreur persiste, veuillez nous contacter.",
    expression: "etourdi",
    mouvements: ["flicker"],
    cta: "Recharger la page",
    cta2: "Nous contacter",
  },
  403: {
    code: "Erreur 403",
    titre: "Séance à huis clos",
    texte:
      "Cette page est réservée aux utilisateurs abonnés à une veille privée. Choisissez une autre veille dans le menu, ou comparez nos abonnements. Une erreur ? Contactez-nous.",
    expression: "severe",
    mouvements: ["shake"],
    accessoire: "lock",
    cta: "Réinitialiser ma veille",
    cta2: "Comparer les abonnements",
  },
} as const satisfies Record<number, ErreurMinerve>;

/** Vrai quand la charte décrit une page pour ce statut (sinon `ErrorPage` n'invente pas de code). */
export function statutDecrit(statut: number | string | undefined): boolean {
  return Number(statut) in ERREURS;
}

/** Compte suspendu : `401` renvoyé par la connexion quand l'utilisateur est banni. */
export const ERREUR_COMPTE_SUSPENDU = {
  code: "Erreur 401 · compte",
  titre: "Compte suspendu",
  texte:
    "L'accès à votre compte est suspendu. Contactez le support Follaw pour en connaître la raison et la durée.",
  expression: "severe",
  mouvements: ["blink"],
  cta: "Contacter le support",
} as const satisfies ErreurMinerve;

/** Hors ligne : branché sur les événements `online` / `offline` du navigateur. */
export const ERREUR_HORS_LIGNE = {
  code: "Hors ligne",
  titre: "Connexion perdue",
  texte:
    "Vous êtes hors ligne. Minerve continue de veiller : les nouveautés s'afficheront dès le retour de la connexion.",
  expression: "triste",
  mouvements: ["breath"],
  accessoire: "wifi",
  cta: "Réessayer",
} as const satisfies ErreurMinerve;

/** L'erreur de la charte pour un code HTTP ; 500 pour tout code que la charte ne décrit pas. */
export function erreurPourStatut(
  statut: number | string | undefined
): ErreurMinerve {
  const code = Number(statut) as keyof typeof ERREURS;
  return ERREURS[code] ?? ERREURS[500];
}
